import { useEffect, useState } from 'react';
import type { SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { Checkbox, Dropdown, DropdownMenu, IconButton, Tooltip } from '@hotjar/hotsauce';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Columns, Column, ColumnKey } from '../../../store/list/types';

const ColumnCheckbox = styled(Checkbox)`
  margin: 0;
`;

export interface ColumnPickerProps {
  onColumnsUpdated: (listColumns: Columns) => void;
  listColumns: Columns;
  trackClick: (elementName: string, uiPosition: string) => void;
}

export const ColumnPicker = ({
  onColumnsUpdated,
  listColumns,
  trackClick,
}: ColumnPickerProps): JSX.Element => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<Columns>(listColumns);

  useEffect(() => {
    setColumns(listColumns);
  }, [listColumns]);

  const trackColumnPicked = (column: Column): void => {
    const sluggedLabel = column.label.toLowerCase().split(' ').join('-').replace('&', 'and');
    const eventName = column.show ? `enable_${sluggedLabel}` : `disable_${sluggedLabel}`;
    trackClick(eventName, `recordings.column.picker_${sluggedLabel}`);
  };

  const onCheckboxChange = (columnName: ColumnKey) => {
    const newColumns = {
      ...columns,
      [columnName]: {
        ...columns[columnName],
        show: !columns[columnName].show,
      },
    };

    setColumns(newColumns);
    onColumnsUpdated(newColumns);

    trackColumnPicked(newColumns[columnName]);
  };

  return (
    <Dropdown
      trigger={
        <IconButton
          icon="table_cols"
          description={t('common:recordings.list-actions.column-picker.tooltip')}
          tooltipText={t('common:recordings.list-actions.column-picker.tooltip')}
          size="s"
          kind="ghost"
          data-testid="recordings-list-actions-columns"
          onClick={() => {
            trackClick('opened_recordings.column.picker', 'recordings.column.picker');
          }}
        />
      }
      zIndex={20}
      css={css`
        list-style-type: none;
        min-width: 180px;
      `}
    >
      {(Object.entries(columns) as [ColumnKey, Column][])
        .filter(([, column]) => column.showInPanel)
        .map(([columnKey, column]) => (
          <DropdownMenu.BaseItem
            onClick={(e: SyntheticEvent) => {
              e.preventDefault();
              if (column.isDisabled) {
                return;
              }

              onCheckboxChange(columnKey);
            }}
            key={columnKey}
          >
            <Tooltip
              placement="left"
              text={t('common:recordings.list-actions.column-picker.upsell-prompt-business')}
              isDisabled={!column.isDisabled}
            >
              <span>
                <ColumnCheckbox
                  isDisabled={column.isDisabled}
                  isChecked={column.show}
                  onChange={() => onCheckboxChange(columnKey)}
                  labelContent={t(`common:recordings.sorting.${column.key}`)}
                />
              </span>
            </Tooltip>
          </DropdownMenu.BaseItem>
        ))}
    </Dropdown>
  );
};
